import React, { useState } from "react";
import api, { formatApiError, formatINR } from "../lib/api";
import { toast } from "sonner";
import { FilePdf, DownloadSimple, Printer, FloppyDisk } from "@phosphor-icons/react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function PayslipModal({ employee, onClose, onSaved }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [basicSalary, setBasicSalary] = useState(employee?.basic_salary ?? 0);
  const [savingSalary, setSavingSalary] = useState(false);

  const generate = async (action) => {
    setDownloading(true);
    try {
      const res = await api.get(`/employees/${employee.id}/payslip`, {
        params: { year, month }, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const filename = `payslip_${employee.employee_id}_${year}_${String(month).padStart(2, "0")}.pdf`;
      if (action === "print") {
        const w = window.open(url, "_blank");
        if (w) { w.onload = () => { try { w.focus(); w.print(); } catch { /* user can print manually */ } }; }
        else { toast.error("Pop-up blocked — allow pop-ups to print"); }
        toast.success("Payslip opened for print");
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
        toast.success(action === "save" ? "Payslip saved as PDF" : "Payslip downloaded");
      }
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Payslip generation failed");
    } finally { setDownloading(false); }
  };

  const download = () => generate("download");
  const save = () => generate("save");
  const print = () => generate("print");

  const saveSalary = async () => {
    setSavingSalary(true);
    try {
      await api.put(`/employees/${employee.id}`, { basic_salary: parseFloat(basicSalary) });
      toast.success("Basic salary updated");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingSalary(false); }
  };

  const years = [];
  for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 4; y--) years.push(y);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="payslip-modal">
      <div className="bg-white w-full max-w-lg border border-[#E5E7EB]">
        <div className="border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-caps">Payslip</div>
            <h3 className="font-display font-bold text-xl">{employee.name}</h3>
            <div className="text-xs text-[#6B7280] mt-0.5">{employee.employee_id} · {employee.department}</div>
          </div>
          <button onClick={onClose} className="text-[#6B7280]" data-testid="payslip-close">✕</button>
        </div>

        <div className="p-6 space-y-5">
          {employee.can_edit_salary && (
            <div className="border border-[#E5E7EB] p-4">
              <div className="label-caps mb-2">Basic salary (₹ / month)</div>
              <div className="flex gap-2">
                <input type="number" min="0" step="100" value={basicSalary}
                       onChange={(e) => setBasicSalary(e.target.value)} data-testid="payslip-basic-salary"
                       className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
                <button onClick={saveSalary} disabled={savingSalary} data-testid="payslip-save-salary"
                        className="px-3 py-2 text-sm border border-[#E5E7EB] hover:bg-[#F9FAFB]">
                  {savingSalary ? "…" : "Save"}
                </button>
              </div>
              <div className="text-xs text-[#6B7280] mt-2">
                Current: {formatINR(employee.basic_salary || 0)} · Sets Basic; HRA (40%) & Special (20%) auto-computed.
              </div>
            </div>
          )}

          {!employee.can_edit_salary && (
            <div className="border border-[#E5E7EB] p-4 text-sm">
              <div className="label-caps mb-1">Basic salary</div>
              <div className="font-display font-bold text-xl">{formatINR(employee.basic_salary || 0)}</div>
              <div className="text-xs text-[#6B7280] mt-1">Contact your admin to update salary details.</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-caps mb-2">Month</div>
              <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                      className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="payslip-month">
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <div className="label-caps mb-2">Year</div>
              <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                      className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="payslip-year">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 border-t border-[#E5E7EB] pt-5">
            <FilePdf size={22} weight="regular" color="#DC2626" />
            <div className="flex-1 text-sm text-[#6B7280]">
              PDF payslip for <b className="text-[#111827]">{MONTHS[month - 1]} {year}</b>. Sales targets & attendance auto-adjust pay.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button onClick={download} disabled={downloading} data-testid="payslip-download"
                    className="brand-btn py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2">
              <DownloadSimple size={16} weight="bold" />
              {downloading ? "Generating…" : "Download PDF"}
            </button>
            <button onClick={save} disabled={downloading} data-testid="payslip-save-pdf"
                    className="border border-[#106109] text-[#106109] hover:bg-[#F0F7EF] py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <FloppyDisk size={16} weight="bold" />
              Save as PDF
            </button>
            <button onClick={print} disabled={downloading} data-testid="payslip-print"
                    className="border border-[#1E40AF] text-[#1E40AF] hover:bg-[#EFF6FF] py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <Printer size={16} weight="bold" />
              Print
            </button>
          </div>
          <div className="text-[11px] text-[#6B7280] text-center">
            Print opens a new tab with the PDF and triggers your browser's print dialog. Allow pop-ups for this site.
          </div>
        </div>
      </div>
    </div>
  );
}
